import React, { useState, useContext, useEffect } from "react";
import Select from "react-select";
import { FilterContext } from "../FilterContextProvider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faArrowLeft, faCheck } from "@fortawesome/free-solid-svg-icons";
import Button from "../Components/Button";
import Input from "../Components/Input";
import ReportsLayout from "./ReportsLayout";
import useEvents from "../hooks/useEvents";
import { exportToCSV } from "../Utility/Csv";
import { Link } from "react-router-dom";
import useApi from "../Api/Api";
import { NotificationManager } from "react-notifications";
import { DateTime } from "luxon";

const Taxes = (props) => {
  const { filterData } = useContext(FilterContext);
  const api = useApi();
  const [alterEventsObj, setAlterEventsObj] = useState({});
  const [errorMessage, setErrorMessage] = useState(false);

  const {
    getEvents,
    events,
    pagy,
    currentPage,
    setCurrentPage,
    eventDates,
    eventDateOptions,
    handleEventDateSelect,
    renderCustomEventDateSelector,
    remittedFilter,
    setRemittedFilter,
    reportedFilter,
    setReportedFilter,
    sortFilter,
    setSortFilter,
    resetPagination,
    setSearchFilter,
  } = useEvents("show", "", props.isCtmTaxable);

  const sortOptions = [
    {
      label: "Date",
      value: "date",
    },
    {
      label: "City",
      value: "city",
    },
    {
      label: "State",
      value: "state",
    },
    {
      label: "Venue",
      value: "name",
    },
    {
      label: "Recently added",
      value: "created_at",
    },
  ];

  const checkedOptions = [
    {
      label: "Yes",
      value: true,
    },
    {
      label: "No",
      value: false,
    },
    {
      label: "All",
      value: null,
    },
  ];

  const handleRemittedFilter = (e) => {
    if (e.value === true) {
      setRemittedFilter(true);
    } else if (e.value === false) {
      setRemittedFilter(false);
    } else {
      setRemittedFilter(null);
    }
  };

  const handleReportedFilter = (e) => {
    if (e.value === true) {
      setReportedFilter(true);
    } else if (e.value === false) {
      setReportedFilter(false);
    } else {
      setReportedFilter(null);
    }
  };

  const handleSortSelect = (option) => {
    if (option.value !== sortFilter) {
      setSortFilter(option.value);
      resetPagination();
      setCurrentPage(1);
    }
  };

  const [searchTimeout, setSearchTimeout] = useState();

  const handleSearchChange = (e) => {
    setSearchTimeout(e.target.value);
  };

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      setSearchFilter(searchTimeout);
    }, 500);

    return () => clearTimeout(delaySearch);
  }, [searchTimeout, setSearchFilter]);

  const handlePageChange = (e) => {
    setCurrentPage(e.target.value);
  };

  const handleCheckChange = (e, eventToRender, state) => {
    setAlterEventsObj({
      ...alterEventsObj,
      [eventToRender.id]: {
        ...eventToRender,
        [e.target.id]: !state,
      },
    });
  };

  const revert = () => {
    const check = window.confirm("Are you sure you want to revert? All unsaved changes will be lost.");
    if (check) {
      setAlterEventsObj({});
    }
  };

  const handleUpdate = (event) => {
    event.preventDefault();
    Promise.all(
      Object.values(alterEventsObj).map((event) => {
        return api.put(`/events/${event.id}`, { event });
      })
    )
      .then((response) => {
        NotificationManager.success("Events successfully updated", "Success", 2000);
        getEvents(currentPage);
        setAlterEventsObj({});
        return response;
      })
      .catch((error) => {
        setErrorMessage(error.response.data);
      })
      .finally(() => {});
  };

  events.forEach((event) => {
    if (event.new_tax_formula) {
      event.reported_amount = event.gross_sales;
    } else {
      event.reported_amount = event.gross_sales - event.tax_net;
    }
  });

  const exportAllEventsToCSV = async () => {
    try {
      const allEvents = await getEvents(null, false, true);

      allEvents.forEach((event) => {
        if (event.new_tax_formula) {
          event.reported_amount = event.gross_sales;
        } else {
          event.reported_amount = event.gross_sales - event.tax_net;
        }
      });

      const csvFormattedEvents = allEvents.map((event) => ({
        Event_Date: DateTime.fromISO(event.date).toUTC().toFormat("MM/dd/yyyy"),
        Artist: event.artist?.name,
        City: event.city,
        County: event.county,
        State: event.state,
        Gross_Sales: event.gross_sales,
        Tax: event.tax_net,
        Reported_Amount: event.reported_amount,
        Tax_Remitted_by_TM: event.remitted,
        Reported: event.reported,
        New_tax_formula: event.new_tax_formula,
      }));

      exportToCSV(
        csvFormattedEvents,
        props.isCtmTaxable ? "total_tax_liability_report" : `${filterData.artist_name}_tax_report`
      );
    } catch (error) {
      console.error("Error exporting all events: ", error);
      // Handle error, such as displaying a notification to the user
    }
  };

  const renderSelectArtistMessage = () => {
    return <div className="m-8 py-12 pl-4">Please select an artist from the filter in order to see events</div>;
  };

  const renderEventsTable = () => {
    return (
      <div>
        <div>
          <div className="max-w-max">
            <Link to="/reports">
              <div className="-mt-6 mb-6 font-medium text-black text-sm">
                <FontAwesomeIcon icon={faArrowLeft}></FontAwesomeIcon> Back to reports
              </div>
            </Link>
          </div>
          <div className="text-xl mt-7 pb-8 font-medium">
            {props.isCtmTaxable ? "Total Tax Liability" : "Tax Report"}
          </div>
          <div className="-mt-5 mb-4 text-sm">
            <div className="flex">
              <div className="absolute mt-6 ml-2">
                <FontAwesomeIcon icon={faSearch}></FontAwesomeIcon>
              </div>
              <div className="w-1/4 text-xs">
                <Input
                  onChange={handleSearchChange}
                  value={searchTimeout}
                  placeholder="Search for events"
                  label="SEARCH"
                  bigPaddingLeft
                />
              </div>
              <div className="flex flex-col w-1/4 ml-2">
                <span className="text-xs">SORT BY</span>
                <Select
                  id="sort_filter"
                  className="text-xs font-light"
                  placeholder="Select sort by..."
                  options={sortOptions}
                  onChange={handleSortSelect}
                  value={sortOptions?.filter((each) => each.value === sortFilter)}
                />
              </div>
              <div className="flex flex-col text-sm w-1/4 ml-2">
                <span className="text-xs">SELECT DATE RANGE</span>
                <Select
                  id="date"
                  className="text-xs font-light w-full"
                  placeholder="Select date range"
                  options={eventDateOptions}
                  onChange={handleEventDateSelect}
                  value={eventDateOptions?.filter((each) => each.value === eventDates.dateButton)}
                />
              </div>
              <div className="flex flex-col text-sm w-1/4 ml-2">
                <span className="text-xs">Remitted?</span>
                <Select
                  id="date"
                  className="text-xs font-light w-full"
                  placeholder="Filter by remitted"
                  options={checkedOptions}
                  onChange={handleRemittedFilter}
                  value={checkedOptions?.filter((each) => each.value === remittedFilter)}
                />
              </div>
              <div className="flex flex-col text-sm w-1/4 ml-2">
                <span className="text-xs">Reported?</span>
                <Select
                  id="date"
                  className="text-xs font-light w-full"
                  placeholder="Filter by reported"
                  options={checkedOptions}
                  onChange={handleReportedFilter}
                  value={checkedOptions?.filter((each) => each.value === reportedFilter)}
                />
              </div>
            </div>
            <div className="my-4 w-1/2">{eventDates.dateButton === "custom" && renderCustomEventDateSelector()}</div>
          </div>
        </div>
        {events.length > 0 ? (
          <div>
            <form>
              <table>
                <thead className="text-xs font-light">
                  <tr className="text-left border-b-2 h-11 w-full">
                    <td className="w-1/8">DATE</td>
                    {props.isCtmTaxable && <td className="w-1/6">ARTIST</td>}
                    <td className="w-1/6">CITY</td>
                    <td className="w-1/6">COUNTY</td>
                    <td className="w-thin">STATE</td>
                    <td className="w-1/8 text-right">GROSS SALES</td>
                    <td className="w-1/8 text-right">GROSS TAX</td>
                    <td className="w-1/8 text-right">REPORTED AMOUNT</td>
                    <td className="w-thin pl-4">TAX REMITTED</td>
                    <td className="w-thin">REPORTED</td>
                    <td className="w-thin">TAX EXCLUSIVE</td>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event, i) => {
                    const eventToRender = alterEventsObj[event.id] || event;
                    return (
                      <tr key={eventToRender.id} className="border-b text-sm h-11">
                        <td id={`date_${i}`}>{eventToRender.formattedDate}</td>
                        {props.isCtmTaxable && <td id={`artist_${i}`}>{eventToRender?.artist?.name}</td>}
                        <td id={`city_${i}`}>{eventToRender?.city}</td>
                        <td id={`county_${i}`}>{eventToRender?.county}</td>
                        <td id={`state_${i}`}>{eventToRender?.state}</td>
                        <td id={`gross_sales_${i}`} className="text-right">
                          {eventToRender.gross_sales
                            ? eventToRender.gross_sales?.toLocaleString("en-US", {
                                style: "currency",
                                currency: "USD",
                              })
                            : 0?.toLocaleString("en-US", {
                                style: "currency",
                                currency: "USD",
                              })}
                        </td>
                        <td id={`tax_net_${i}`} className="text-right">
                          {eventToRender.tax_net
                            ? eventToRender.tax_net?.toLocaleString("en-US", {
                                style: "currency",
                                currency: "USD",
                              })
                            : 0?.toLocaleString("en-US", {
                                style: "currency",
                                currency: "USD",
                              })}
                        </td>
                        <td id={`reported_amount_${i}`} className="text-right">
                          {eventToRender.reported_amount
                            ? eventToRender.reported_amount?.toLocaleString("en-US", {
                                style: "currency",
                                currency: "USD",
                              })
                            : 0?.toLocaleString("en-US", {
                                style: "currency",
                                currency: "USD",
                              })}
                        </td>
                        <td id={`remitted_${i}`} className="text-center pl-4">
                          <input
                            className="mt-1"
                            type="checkbox"
                            id="remitted"
                            checked={!!eventToRender?.remitted}
                            onChange={(e) => {
                              handleCheckChange(e, eventToRender, eventToRender.remitted);
                            }}
                          />
                        </td>
                        <td id={`reported_${i}`} className="text-center">
                          <input
                            className="mt-1"
                            type="checkbox"
                            id="reported"
                            checked={!!eventToRender?.reported}
                            onChange={(e) => {
                              handleCheckChange(e, eventToRender, eventToRender.reported);
                            }}
                          />
                        </td>
                        <td id={`new_tax_formula_${i}`} className="text-center">
                          {eventToRender?.new_tax_formula && <FontAwesomeIcon icon={faCheck}></FontAwesomeIcon>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </form>
            {Object.keys(alterEventsObj).length !== 0 && (
              <span className="flex justify-center mt-4">
                <span className="mr-2">
                  <Button className="button button-white" onClick={revert} text="Revert"></Button>
                </span>
                <Button className="button" onClick={handleUpdate} text="Update"></Button>
              </span>
            )}
          </div>
        ) : sortFilter ? (
          <div className="mx-12 py-16">No events match the provided filters</div>
        ) : (
          <div className="mx-12 py-16">No events have been added for this artist yet</div>
        )}
      </div>
    );
  };

  return (
    <ReportsLayout>
      <div>
        <div className="mt-8 mx-8 py-10 px-12 bg-white text-black rounded-lg border">
          {events.length > 0 && (
            <div className="flex justify-end text-sm">
              <Button className="button" text="Export" onClick={exportAllEventsToCSV}></Button>
            </div>
          )}
          {filterData.artist_id ? renderEventsTable() : renderSelectArtistMessage()}
        </div>
        {events.length > 0 && (
          <>
            <div className="grid justify-items-center">
              {errorMessage.error && <div className="mt-4 text-red-500 pb-2">{errorMessage.error}</div>}
            </div>
            <footer className="text-sm mt-4 mx-8 pb-4 flex justify-between">
              <div className="ml-1 mr-8">
                Showing <span className="font-bold">{pagy.from} </span>to
                <span className="font-bold"> {pagy.to} </span>of
                <span className="font-bold"> {pagy.count} </span>results
              </div>
              <div className="mr-8">
                Page {pagy.page} of {pagy.last}
              </div>
              <div className="flex -mt-2">
                <span>
                  <Button
                    className={pagy.page === 1 ? "button-white-disabled button-sm" : "button button-white button-sm"}
                    onClick={resetPagination}
                    text="First"
                    disabled={pagy.page === 1}
                  ></Button>
                </span>
                <span className="mx-1">
                  <Button
                    value={pagy.prev}
                    className={!pagy.prev ? "button-white-disabled button-sm" : "button button-white button-sm"}
                    onClick={handlePageChange}
                    text="Prev"
                    disabled={!pagy.prev}
                  ></Button>
                </span>
                <span>
                  <Button
                    value={pagy.next}
                    className={!pagy.next ? "button-white-disabled button-sm" : "button button-white button-sm"}
                    onClick={handlePageChange}
                    text="Next"
                    disabled={!pagy.next}
                  ></Button>
                </span>
                <span className="ml-1">
                  <Button
                    value={pagy.last}
                    className={
                      pagy.page === pagy.last ? "button-white-disabled button-sm" : "button button-white button-sm"
                    }
                    onClick={handlePageChange}
                    text="Last"
                    disabled={pagy.page === pagy.last}
                  ></Button>
                </span>
              </div>
            </footer>
          </>
        )}
      </div>
    </ReportsLayout>
  );
};

export default Taxes;
